import React, { useState, useEffect, useCallback } from 'react';
import {
  Page,
  Layout,
  Card,
  Tabs,
  EmptyState,
  Text,
  Button,
  LegacyStack,
  Spinner,
  Banner,
  Badge
} from '@shopify/polaris';
import { useShop } from '../../contexts/ShopContext';
import { useAuth } from '../../contexts/AuthContext';
import { getSizechartGroups, getSizecharts, getSizechartSettings } from '../../services/sizechartService';
import SizechartDesigner from './SizechartDesigner';
import SizechartChecker from './SizechartChecker';
import SizechartSettings from './SizechartSettings';
import ProductChartGenerator from './ProductChartGenerator';
import LanguageSettings from './LanguageSettings';
import SizeChartDesignerNew from './SizeChartDesignerNew';
import { ListMajor } from '@shopify/polaris-icons';

export default function SizechartManager() {
  const { currentShop } = useShop();
  const { user } = useAuth();
  const [selected, setSelected] = useState(0);
  const [groups, setGroups] = useState([]);
  const [sizecharts, setSizecharts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load sizechart data when shop changes
  useEffect(() => {
    if (currentShop) {
      loadSizechartData();
    }
  }, [currentShop]);

  // Load all sizechart data for the current shop
  const loadSizechartData = async () => {
    if (!currentShop) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Load groups, sizecharts, and settings in parallel
      const [groupsData, sizechartsData, settingsData] = await Promise.all([
        getSizechartGroups(currentShop.id),
        getSizecharts(currentShop.id),
        getSizechartSettings(currentShop.id)
      ]);
      
      setGroups(groupsData);
      setSizecharts(sizechartsData);
      setSettings(settingsData);
    } catch (err) {
      console.error('Error loading sizechart data:', err);
      setError('Failed to load sizechart data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle tab change
  const handleTabChange = useCallback((selectedTabIndex) => {
    setSelected(selectedTabIndex);
  }, []);

  // Define tabs
  const tabs = [
    {
      id: 'creator',
      content: 'Size Chart Creator',
      accessibilityLabel: 'Size Chart Creator',
      panelID: 'creator-panel',
    },
    {
      id: 'compare',
      content: 'Size Chart Compare',
      accessibilityLabel: 'Size Chart Compare',
      panelID: 'compare-panel',
    },
    {
      id: 'settings',
      content: 'Settings',
      accessibilityLabel: 'Sizechart Settings',
      panelID: 'settings-panel',
    },
    {
      id: 'languages',
      content: 'Languages',
      accessibilityLabel: 'Language Settings',
      panelID: 'languages-panel',
    },
  ];

  // Define subtabs for Size Chart Compare
  const [compareSelected, setCompareSelected] = useState(0);
  const handleCompareTabChange = useCallback((selectedTabIndex) => {
    setCompareSelected(selectedTabIndex);
  }, []);

  // Define subtabs for Size Chart Creator
  const [creatorSelected, setCreatorSelected] = useState(0);
  const handleCreatorTabChange = useCallback((selectedTabIndex) => {
    setCreatorSelected(selectedTabIndex);
  }, []);

  const compareTabs = [
    {
      id: 'checker',
      content: 'Product Size Check',
      accessibilityLabel: 'Product Size Check',
      panelID: 'checker-panel',
    },
    {
      id: 'designer',
      content: 'Master Templates',
      accessibilityLabel: 'Master Templates',
      panelID: 'designer-panel',
    },
  ];

  const creatorTabs = [
    {
      id: 'generator',
      content: 'Product Chart Generator',
      accessibilityLabel: 'Product Chart Generator',
      panelID: 'generator-panel',
    },
    {
      id: 'designer',
      content: 'Size Chart Designer',
      accessibilityLabel: 'Size Chart Designer',
      panelID: 'designer-panel',
    },
    {
      id: 'generated',
      content: 'Generated Charts',
      accessibilityLabel: 'Generated Charts',
      panelID: 'generated-panel',
    },
  ];

  // Render empty state if no shop is selected
  if (!currentShop) {
    return (
      <Page title="Sizechart Manager" subtitle="No shop selected">
        <Layout>
          <Layout.Section>
            <Card>
              <Card.Section>
                <EmptyState
                  heading="No shop selected"
                  image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                >
                  <p>Please select a shop to manage sizecharts.</p>
                </EmptyState>
              </Card.Section>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  // Render loading state
  if (loading) {
    return (
      <Page
        title="Sizechart Manager"
        subtitle={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Text variant="bodyMd">Loading sizechart data...</Text>
          </div>
        }
      >
        <Layout>
          <Layout.Section>
            <Card>
              <Card.Section>
                <LegacyStack distribution="center" spacing="loose">
                  <Spinner size="large" />
                  <Text variant="headingMd">Loading sizechart data...</Text>
                </LegacyStack>
              </Card.Section>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  // Render error state
  if (error) {
    return (
      <Page
        title="Sizechart Manager"
        subtitle={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Text variant="bodyMd" color="critical">Error loading data</Text>
          </div>
        }
      >
        <Layout>
          <Layout.Section>
            <Banner status="critical" title="Error loading data">
              <p>{error}</p>
              <Button onClick={loadSizechartData}>Try Again</Button>
            </Banner>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <Page title="Sizechart Manager">
      <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Text variant="bodyMd">Managing sizecharts for</Text>
        <Badge status="success">
          {currentShop.name || currentShop.shop}
        </Badge>
      </div>
      <Tabs tabs={tabs} selected={selected} onSelect={handleTabChange}>
        {selected === 0 && (
          <Card padding="0">
            <div style={{ height: 'auto', overflow: 'visible' }}>
              <Tabs tabs={creatorTabs} selected={creatorSelected} onSelect={handleCreatorTabChange}>
                {creatorSelected === 0 && (
                  <div style={{ padding: '16px' }}>
                    <ProductChartGenerator
                      shopId={currentShop.id}
                      userId={user.uid}
                      settings={settings}
                      refreshData={loadSizechartData}
                    />
                  </div>
                )}
                {creatorSelected === 1 && (
                  <div style={{ padding: '16px' }}>
                    <SizeChartDesignerNew
                      shopId={currentShop.id}
                      userId={user.uid}
                      settings={settings}
                      refreshData={loadSizechartData}
                      showTemplateDesignOnly={true}
                    />
                  </div>
                )}
                {creatorSelected === 2 && (
                  <div style={{ padding: '16px' }}>
                    <SizeChartDesignerNew
                      shopId={currentShop.id}
                      userId={user.uid}
                      settings={settings}
                      refreshData={loadSizechartData}
                      showGeneratedChartsOnly={true}
                    />
                  </div>
                )}
              </Tabs>
            </div>
          </Card>
        )}
        {selected === 1 && (
          <Card padding="0">
            <div style={{ height: 'auto', overflow: 'visible' }}>
              <Tabs tabs={compareTabs} selected={compareSelected} onSelect={handleCompareTabChange}>
                {compareSelected === 0 && (
                  <div style={{ padding: '16px' }}>
                    <SizechartChecker 
                      shopId={currentShop.id} 
                      userId={user.uid}
                      groups={groups} 
                      sizecharts={sizecharts} 
                      settings={settings} 
                      refreshData={loadSizechartData} 
                    />
                  </div>
                )}
                {compareSelected === 1 && (
                  <div style={{ padding: '16px' }}>
                    <SizechartDesigner
                      shopId={currentShop.id}
                      userId={user.uid}
                      groups={groups}
                      setGroups={setGroups}
                      sizecharts={sizecharts}
                      setSizecharts={setSizecharts}
                      refreshData={loadSizechartData}
                      settings={settings}
                    />
                  </div>
                )}
              </Tabs>
            </div>
          </Card>
        )}
        {selected === 2 && (
          <Card padding="0">
            <div style={{ height: 'auto', overflow: 'visible' }}>
              <div style={{ padding: '16px' }}>
                <SizechartSettings 
                  shopId={currentShop.id} 
                  settings={settings} 
                  setSettings={setSettings} 
                  refreshData={loadSizechartData} 
                />
              </div>
            </div>
          </Card>
        )}
        {selected === 3 && (
          <Card padding="0">
            <div style={{ height: 'auto', overflow: 'visible' }}>
              <div style={{ padding: '16px' }}>
                <LanguageSettings 
                  shopId={currentShop.id} 
                  userId={user.uid}
                  settings={settings}
                  refreshData={loadSizechartData}
                />
              </div>
            </div>
          </Card>
        )}
      </Tabs>
    </Page>
  );
}
